import React, { useState, useMemo, useEffect } from "react";
import { Card, Tag, Button, Space, Typography, Row, Col, Statistic, Spin, Alert, Input, Select, Avatar, message, Collapse, Tabs, Table, Modal, Form, Switch, InputNumber, Tooltip, Popconfirm } from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
    EyeOutlined,
    BarChartOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    TeamOutlined,
    MessageOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    GlobalOutlined,
    StarOutlined,
    StarFilled,
} from "@ant-design/icons";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
    useGetAdminSurveysQuery,
    useGetAdminSurveyCohortsQuery,
    useGetAdminFinalQuestionsQuery,
    useCreateAdminFinalQuestionMutation,
    useUpdateAdminFinalQuestionMutation,
    useDeleteAdminFinalQuestionMutation,
    useBulkApplyFinalQuestionMutation,
} from "../../services/nestjs-base-api";
import { IAdminSurveyResponse, IAdminSurveyCohortResponse } from "../../services/nestjs-base-api";
import { IFinalQuestion } from "../../services/nestjs-base-api/types";
import { Utils } from "../../utils/utils";
import { renderFlagIcon, getLanguageName } from "../../constants/countries";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export const AdminSurveysPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("surveys");
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [organizationFilter, setOrganizationFilter] = useState<string | undefined>(undefined);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 50;

    // Cohorts query
    const { data: cohortsData, isLoading: cohortsLoading, refetch: refetchCohorts } = useGetAdminSurveyCohortsQuery({
        jwtToken: user?.jwtToken!,
    }, {
        skip: !user?.jwtToken
    });

    // Final Questions state and queries
    const [fqCurrentPage, setFqCurrentPage] = useState(1);
    const [fqPageSize, setFqPageSize] = useState(10);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<IFinalQuestion | null>(null);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const isSuperAdmin = Utils.userIsSuperAdmin(user);

    const { data: questionsData, isLoading: questionsLoading, refetch: refetchQuestions } = useGetAdminFinalQuestionsQuery({
        jwtToken: user?.jwtToken || '',
        page: fqCurrentPage,
        limit: fqPageSize,
        includeInactive: true,
    }, {
        skip: !user?.jwtToken
    });

    const [createQuestion, { isLoading: isCreating }] = useCreateAdminFinalQuestionMutation();
    const [updateQuestion, { isLoading: isUpdating }] = useUpdateAdminFinalQuestionMutation();
    const [deleteQuestion] = useDeleteAdminFinalQuestionMutation();
    const [bulkApplyQuestion, { isLoading: isBulkApplying }] = useBulkApplyFinalQuestionMutation();

    const [isApplyToAllModalVisible, setIsApplyToAllModalVisible] = useState(false);
    const [questionToApply, setQuestionToApply] = useState<IFinalQuestion | null>(null);

    const questions = questionsData?.data || [];
    const questionsTotal = questionsData?.total || 0;
    const surveysWithoutFinalQuestion = questionsData?.surveysWithoutFinalQuestion || 0;

    const { data: surveysData, isLoading, error, refetch } = useGetAdminSurveysQuery({
        jwtToken: user?.jwtToken!,
        search: searchText || undefined,
        status: statusFilter,
        organisationId: organizationFilter ? parseInt(organizationFilter) : undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        sortBy: 'createdAt',
        sortOrder: 'DESC'
    }, {
        skip: !user?.jwtToken
    });

    const surveys = useMemo(() => surveysData?.data || [], [surveysData?.data]);
    const total = surveysData?.total || 0;

    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'active': return 'green';
            case 'draft': return 'default';
            case 'paused': return 'orange';
            case 'completed': return 'blue';
            default: return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active': return <PlayCircleOutlined />;
            case 'paused': return <PauseCircleOutlined />;
            case 'completed': return <CheckCircleOutlined />;
            case 'draft': return <CloseCircleOutlined />;
            default: return null;
        }
    };

    // Final Questions handlers
    const handleCreateQuestion = async (values: any) => {
        try {
            await createQuestion({
                jwtToken: user!.jwtToken,
                payload: {
                    text: values.text,
                    displayOrder: values.displayOrder || 0,
                    isActive: values.isActive ?? true,
                },
            }).unwrap();
            message.success('Final question created successfully');
            setIsCreateModalVisible(false);
            form.resetFields();
            refetchQuestions();
        } catch (error: any) {
            console.error('Failed to create final question:', error);
            message.error(error?.data?.message || 'Failed to create final question');
        }
    };

    const handleEditQuestion = async (values: any) => {
        if (!selectedQuestion) return;

        try {
            await updateQuestion({
                jwtToken: user!.jwtToken,
                id: selectedQuestion.id,
                payload: {
                    text: values.text,
                    displayOrder: values.displayOrder,
                    isActive: values.isActive,
                },
            }).unwrap();
            message.success('Final question updated successfully');
            setIsEditModalVisible(false);
            setSelectedQuestion(null);
            editForm.resetFields();
            refetchQuestions();
        } catch (error: any) {
            console.error('Failed to update final question:', error);
            message.error(error?.data?.message || 'Failed to update final question');
        }
    };

    const handleDeleteQuestion = async (id: number) => {
        try {
            await deleteQuestion({
                jwtToken: user!.jwtToken,
                id,
            }).unwrap();
            message.success('Final question deleted successfully');
            refetchQuestions();
        } catch (error: any) {
            console.error('Failed to delete final question:', error);
            message.error(error?.data?.message || 'Failed to delete final question');
        }
    };

    const handleToggleQuestionActive = async (question: IFinalQuestion) => {
        try {
            await updateQuestion({
                jwtToken: user!.jwtToken,
                id: question.id,
                payload: {
                    isActive: !question.isActive,
                },
            }).unwrap();
            message.success(`Final question ${!question.isActive ? 'activated' : 'deactivated'} successfully`);
            refetchQuestions();
        } catch (error: any) {
            console.error('Failed to toggle final question status:', error);
            message.error(error?.data?.message || 'Failed to update status');
        }
    };

    const handleToggleDefault = async (question: IFinalQuestion) => {
        try {
            await updateQuestion({
                jwtToken: user!.jwtToken,
                id: question.id,
                payload: {
                    useAsDefault: !question.useAsDefault,
                },
            }).unwrap();
            message.success(
                question.useAsDefault
                    ? 'Default status removed'
                    : 'Set as default for new surveys'
            );
            refetchQuestions();
        } catch (error: any) {
            console.error('Failed to toggle default status:', error);
            message.error(error?.data?.message || 'Failed to update default status');
        }
    };

    const openEditModal = (question: IFinalQuestion) => {
        setSelectedQuestion(question);
        editForm.setFieldsValue({
            text: question.text,
            displayOrder: question.displayOrder,
            isActive: question.isActive,
        });
        setIsEditModalVisible(true);
    };

    const openApplyToAllModal = (question: IFinalQuestion) => {
        setQuestionToApply(question);
        setIsApplyToAllModalVisible(true);
    };

    const handleBulkApply = async () => {
        if (!questionToApply) return;

        try {
            const result = await bulkApplyQuestion({
                jwtToken: user!.jwtToken,
                id: questionToApply.id,
            }).unwrap();
            message.success(result.message);
            setIsApplyToAllModalVisible(false);
            setQuestionToApply(null);
            refetchQuestions();
        } catch (error: any) {
            console.error('Failed to bulk apply final question:', error);
            message.error(error?.data?.message || 'Failed to apply final question to surveys');
        }
    };

    // Final Questions table columns
    const finalQuestionColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Question Text',
            dataIndex: 'text',
            key: 'text',
            ellipsis: true,
            render: (text: string) => (
                <Tooltip title={text}>
                    <Text>{text.length > 100 ? `${text.substring(0, 100)}...` : text}</Text>
                </Tooltip>
            ),
        },
        {
            title: 'Display Order',
            dataIndex: 'displayOrder',
            key: 'displayOrder',
            width: 130,
            align: 'center' as const,
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 120,
            align: 'center' as const,
            render: (isActive: boolean) => (
                <Space>
                    {isActive ? (
                        <>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <Text>Active</Text>
                        </>
                    ) : (
                        <>
                            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                            <Text type="secondary">Inactive</Text>
                        </>
                    )}
                </Space>
            ),
        },
        {
            title: 'Default',
            dataIndex: 'useAsDefault',
            key: 'useAsDefault',
            width: 100,
            align: 'center' as const,
            render: (useAsDefault: boolean, record: IFinalQuestion) => (
                useAsDefault ? (
                    <Tooltip title="This question is pre-selected for new surveys">
                        <Tag color="gold" icon={<StarFilled />}>
                            Default
                        </Tag>
                    </Tooltip>
                ) : null
            ),
        },
        {
            title: 'Usage',
            dataIndex: 'usageCount',
            key: 'usageCount',
            width: 100,
            align: 'center' as const,
            render: (count: number) => (
                <Tag color={count > 0 ? 'blue' : 'default'}>
                    {count} {count === 1 ? 'survey' : 'surveys'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 280,
            align: 'center' as const,
            render: (_: any, record: IFinalQuestion) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title={record.isActive ? 'Deactivate' : 'Activate'}>
                        <Button
                            type="text"
                            icon={record.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                            onClick={() => handleToggleQuestionActive(record)}
                        />
                    </Tooltip>
                    {record.isActive && (
                        <Tooltip title={record.useAsDefault ? 'Remove as default' : 'Set as default for new surveys'}>
                            <Button
                                type="text"
                                icon={record.useAsDefault ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                                onClick={() => handleToggleDefault(record)}
                            />
                        </Tooltip>
                    )}
                    {record.isActive && surveysWithoutFinalQuestion > 0 && (
                        <Tooltip title={`Apply to ${surveysWithoutFinalQuestion} surveys without a final question`}>
                            <Button
                                type="text"
                                icon={<GlobalOutlined />}
                                onClick={() => openApplyToAllModal(record)}
                            />
                        </Tooltip>
                    )}
                    {isSuperAdmin && (
                        (record.usageCount || 0) > 0 ? (
                            <Tooltip title={`Cannot delete: used by ${record.usageCount} survey(s)`}>
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    disabled
                                />
                            </Tooltip>
                        ) : (
                            <Popconfirm
                                title="Delete final question"
                                description="Are you sure you want to delete this final question?"
                                onConfirm={() => handleDeleteQuestion(record.id)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Tooltip title="Delete">
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        )
                    )}
                </Space>
            ),
        },
    ];

    const handleViewSurvey = (survey: IAdminSurveyResponse) => {
        // Navigate to admin survey details page
        navigate(`/admin/surveys/${survey.hash}`);
    };

    const handleViewResults = (survey: IAdminSurveyResponse) => {
        // Navigate to admin survey results page
        navigate(`/admin/surveys/${survey.hash}/results`);
    };

    const handleCopySurveyUrl = (survey: IAdminSurveyResponse) => {
        const url = Utils.getPublicSurveyUrl(survey.hash);
        navigator.clipboard.writeText(url);
        message.success('Survey URL copied to clipboard!');
    };

    const renderSurveyCard = (survey: IAdminSurveyResponse) => (
        <Col xs={24} sm={12} lg={8} xl={6} key={survey.id}>
            <Card
                className={`admin-survey-card ${survey.isMock ? 'mock-survey-card' : ''}`}
                hoverable
                style={{ 
                    height: '100%', 
                    cursor: 'pointer',
                    ...(survey.isMock ? {
                        backgroundColor: '#fff7e6',
                        borderLeft: '4px solid #fa8c16',
                        borderColor: '#fa8c16'
                    } : {})
                }}
                onClick={() => handleViewSurvey(survey)}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                            <Avatar 
                                size="small" 
                                style={{ backgroundColor: '#6366f1' }}
                            >
                                {survey.brandName?.charAt(0)}
                            </Avatar>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                    fontSize: '14px', 
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span>{survey.name}</span>
                                    {survey.isMock && (
                                        <Tag color="orange" style={{fontSize: '10px', flexShrink: 0}}>
                                            MOCK DATA
                                        </Tag>
                                    )}
                                </div>
                                <div style={{ 
                                    fontSize: '12px', 
                                    color: '#6b7280',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {survey.organisationName}
                                </div>
                            </div>
                        </div>
                        <Tag 
                            color={getStatusColor(survey.status)} 
                            icon={getStatusIcon(survey.status)}
                            style={{ flexShrink: 0, marginLeft: 8 }}
                        >
                            {survey.status}
                        </Tag>
                    </div>
                }
                actions={[
                    <Button 
                        key="view"
                        type="text" 
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewSurvey(survey);
                        }}
                    >
                        View
                    </Button>,
                    survey.status === 'active' || survey.status === 'completed' ? (
                        <Button 
                            key="results"
                            type="text" 
                            icon={<BarChartOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewResults(survey);
                            }}
                        >
                            Results
                        </Button>
                    ) : null
                ].filter(Boolean)}
            >
                <div style={{ minHeight: '140px' }}>
                    <div style={{ marginBottom: 12 }}>
                        <Text strong>Brand:</Text> {survey.brandName}
                    </div>

                    {survey.cohortName && (
                        <div style={{ marginBottom: 12 }}>
                            <Tag color={survey.cohortName === 'All Customers' ? 'green' : 'blue'} style={{ fontSize: '12px' }}>
                                {survey.cohortName}
                            </Tag>
                        </div>
                    )}

                    {(survey.countryCode || survey.languageCode) && (
                        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {survey.countryCode && renderFlagIcon(survey.countryCode, 16)}
                            {survey.languageCode && (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                    {getLanguageName(survey.languageCode)}
                                </span>
                            )}
                        </div>
                    )}

                    {survey.description && (
                        <p style={{ 
                            color: '#6b7280', 
                            fontSize: '12px', 
                            marginBottom: 12,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}>
                            {survey.description}
                        </p>
                    )}
                    
                    <Row gutter={16} style={{ marginTop: 16 }}>
                        <Col span={12}>
                            <Statistic
                                title="Responses"
                                value={survey.responseCount}
                                valueStyle={{ fontSize: '16px' }}
                            />
                        </Col>
                        <Col span={12}>
                            <Statistic
                                title="Accesses"
                                value={survey.accessCount}
                                valueStyle={{ fontSize: '16px' }}
                            />
                        </Col>
                    </Row>

                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: 12 }}>
                        <div>Created: {new Date(survey.createdAt).toLocaleDateString()}</div>
                    </div>
                </div>
            </Card>
        </Col>
    );

    // Get unique organizations for filter
    const uniqueOrganizations = useMemo(() => {
        const orgs = new Map();
        surveys.forEach(survey => {
            if (!orgs.has(survey.organisationId)) {
                orgs.set(survey.organisationId, survey.organisationName);
            }
        });
        return Array.from(orgs, ([id, name]) => ({ id, name }));
    }, [surveys]);

    // Group surveys by brand
    const groupSurveysByBrand = (surveyList: IAdminSurveyResponse[]) => {
        const grouped = surveyList.reduce((acc, survey) => {
            const brandKey = `${survey.brandId}`;
            if (!acc[brandKey]) {
                acc[brandKey] = {
                    brandId: survey.brandId,
                    brandName: survey.brandName,
                    organisationName: survey.organisationName,
                    surveys: []
                };
            }
            acc[brandKey].surveys.push(survey);
            return acc;
        }, {} as Record<string, { brandId: number; brandName: string; organisationName: string; surveys: IAdminSurveyResponse[] }>);

        // Sort by brand name
        return Object.values(grouped).sort((a, b) => a.brandName.localeCompare(b.brandName));
    };

    const surveysByBrand = useMemo(() => groupSurveysByBrand(surveys), [surveys]);

    // CSS for mock data styling
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .mock-survey-card:hover {
                background-color: #ffe7ba !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error loading surveys"
                    description="There was an error loading the surveys. Please try again."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    // Cohorts table columns
    const cohortColumns = [
        {
            title: 'Name',
            dataIndex: 'displayName',
            key: 'displayName',
            render: (text: string, record: IAdminSurveyCohortResponse) => (
                <div>
                    <Text strong>{text}</Text>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{record.code}</div>
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'cohortType',
            key: 'cohortType',
            render: (type: string) => (
                <Tag color={type === 'system' ? 'blue' : type === 'brand' ? 'purple' : 'green'}>
                    {type}
                </Tag>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 300,
            render: (text: string) => (
                <Text style={{ fontSize: '13px' }}>{text || '-'}</Text>
            ),
        },
        {
            title: 'Surveys',
            dataIndex: 'surveyCount',
            key: 'surveyCount',
            align: 'center' as const,
            render: (count: number) => (
                <Tag color={count > 0 ? 'green' : 'default'}>{count}</Tag>
            ),
        },
        {
            title: 'Organisation',
            key: 'organisation',
            render: (_: any, record: IAdminSurveyCohortResponse) => (
                <Text>{record.organisationName || '-'}</Text>
            ),
        },
        {
            title: 'Brand',
            key: 'brand',
            render: (_: any, record: IAdminSurveyCohortResponse) => (
                <Text>{record.brandName || '-'}</Text>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleDateString(),
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '24px' }}>
                <Title level={2}><FileTextOutlined style={{ marginRight: '8px' }} />Platform Surveys</Title>
                <Text type="secondary">
                    Manage and monitor all surveys and cohorts across the platform
                </Text>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: 'surveys',
                        label: (
                            <span>
                                <FileTextOutlined style={{ marginRight: 8 }} />
                                Surveys
                            </span>
                        ),
                        children: (
                            <>
                                {/* Filters */}
                                <Card style={{ marginBottom: '24px' }}>
                                    <Row gutter={16} align="middle">
                                        <Col xs={24} sm={12} md={8} lg={6}>
                                            <Input
                                                placeholder="Search surveys..."
                                                prefix={<SearchOutlined />}
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                                onPressEnter={() => refetch()}
                                                allowClear
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} md={8} lg={6}>
                                            <Select
                                                placeholder="Filter by status"
                                                style={{ width: '100%' }}
                                                value={statusFilter}
                                                onChange={setStatusFilter}
                                                allowClear
                                            >
                                                <Option value="active">Active</Option>
                                                <Option value="draft">Draft</Option>
                                                <Option value="paused">Paused</Option>
                                                <Option value="completed">Completed</Option>
                                            </Select>
                                        </Col>
                                        <Col xs={24} sm={12} md={8} lg={6}>
                                            <Select
                                                placeholder="Filter by organization"
                                                style={{ width: '100%' }}
                                                value={organizationFilter}
                                                onChange={setOrganizationFilter}
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) => {
                                                    const children = option?.children?.toString();
                                                    return children ? children.toLowerCase().includes(input.toLowerCase()) : false;
                                                }}
                                            >
                                                {uniqueOrganizations.map(org => (
                                                    <Option key={org.id} value={org.id.toString()}>
                                                        {org.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Col>
                                        <Col xs={24} sm={12} md={8} lg={6}>
                                            <Space>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    onClick={() => refetch()}
                                                    loading={isLoading}
                                                >
                                                    Refresh
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Statistics */}
                                <Row gutter={16} style={{ marginBottom: '24px' }}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Card>
                                            <Statistic
                                                title="Total Surveys"
                                                value={total}
                                                loading={isLoading}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Card>
                                            <Statistic
                                                title="Active Surveys"
                                                value={surveys.filter(s => s.status === 'active').length}
                                                valueStyle={{ color: '#3f8600' }}
                                                loading={isLoading}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Card>
                                            <Statistic
                                                title="Total Responses"
                                                value={surveys.reduce((sum, s) => sum + s.responseCount, 0)}
                                                loading={isLoading}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Card>
                                            <Statistic
                                                title="Total Accesses"
                                                value={surveys.reduce((sum, s) => sum + s.accessCount, 0)}
                                                loading={isLoading}
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Surveys Grid */}
                                {isLoading ? (
                                    <div style={{ textAlign: 'center', padding: '48px' }}>
                                        <Spin size="large" />
                                    </div>
                                ) : surveys.length === 0 ? (
                                    <Card>
                                        <div style={{ textAlign: 'center', padding: '48px' }}>
                                            <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
                                            <p style={{ fontSize: '16px', color: '#6b7280' }}>
                                                No surveys found matching your criteria
                                            </p>
                                        </div>
                                    </Card>
                                ) : (
                                    <>
                                        {/* Surveys grouped by brand */}
                                        <Collapse
                                            ghost
                                            defaultActiveKey={surveysByBrand.map(group => `brand-${group.brandId}`)}
                                            style={{ marginBottom: 32 }}
                                            items={surveysByBrand.map(group => ({
                                                key: `brand-${group.brandId}`,
                                                label: (
                                                    <span style={{ fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar size="small" style={{ backgroundColor: '#6366f1' }}>
                                                            {group.brandName.charAt(0)}
                                                        </Avatar>
                                                        {group.brandName}
                                                        <Text type="secondary" style={{ fontSize: '13px' }}>
                                                            ({group.organisationName})
                                                        </Text>
                                                        <Tag color="blue" style={{ marginLeft: 8 }}>
                                                            {group.surveys.length}
                                                        </Tag>
                                                    </span>
                                                ),
                                                children: (
                                                    <Row gutter={[16, 16]}>
                                                        {group.surveys.map(renderSurveyCard)}
                                                    </Row>
                                                ),
                                            }))}
                                        />

                                        {/* Pagination */}
                                        <div style={{ textAlign: 'center', marginTop: '32px' }}>
                                            <Space>
                                                <Button
                                                    disabled={currentPage === 1}
                                                    onClick={() => setCurrentPage(currentPage - 1)}
                                                >
                                                    Previous
                                                </Button>
                                                <Text>
                                                    Page {currentPage} of {Math.ceil(total / pageSize)}
                                                </Text>
                                                <Button
                                                    disabled={currentPage >= Math.ceil(total / pageSize)}
                                                    onClick={() => setCurrentPage(currentPage + 1)}
                                                >
                                                    Next
                                                </Button>
                                            </Space>
                                        </div>
                                    </>
                                )}
                            </>
                        ),
                    },
                    {
                        key: 'cohorts',
                        label: (
                            <span>
                                <TeamOutlined style={{ marginRight: 8 }} />
                                Cohorts
                            </span>
                        ),
                        children: (
                            <>
                                <Card style={{ marginBottom: '24px' }}>
                                    <Row gutter={16} align="middle" justify="space-between">
                                        <Col>
                                            <Text type="secondary">
                                                Survey cohorts define customer segments for targeting surveys.
                                                System cohorts are available to all organisations, while brand cohorts are custom.
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Button
                                                icon={<ReloadOutlined />}
                                                onClick={() => refetchCohorts()}
                                                loading={cohortsLoading}
                                            >
                                                Refresh
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Statistics */}
                                <Row gutter={16} style={{ marginBottom: '24px' }}>
                                    <Col xs={24} sm={8}>
                                        <Card>
                                            <Statistic
                                                title="Total Cohorts"
                                                value={cohortsData?.length || 0}
                                                loading={cohortsLoading}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Card>
                                            <Statistic
                                                title="System Cohorts"
                                                value={cohortsData?.filter(c => c.cohortType === 'system').length || 0}
                                                valueStyle={{ color: '#F59E0B' }}
                                                loading={cohortsLoading}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Card>
                                            <Statistic
                                                title="Custom Cohorts"
                                                value={cohortsData?.filter(c => c.cohortType !== 'system').length || 0}
                                                valueStyle={{ color: '#722ed1' }}
                                                loading={cohortsLoading}
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                <Card>
                                    <Table
                                        dataSource={cohortsData || []}
                                        columns={cohortColumns}
                                        rowKey="id"
                                        loading={cohortsLoading}
                                        pagination={{ pageSize: 20 }}
                                    />
                                </Card>
                            </>
                        ),
                    },
                    {
                        key: 'final-questions',
                        label: (
                            <span>
                                <MessageOutlined style={{ marginRight: 8 }} />
                                Final Questions
                            </span>
                        ),
                        children: (
                            <>
                                <Card style={{ marginBottom: '24px' }}>
                                    <Row gutter={16} align="middle" justify="space-between">
                                        <Col>
                                            <Text type="secondary">
                                                Manage optional open-ended questions that can be added to surveys.
                                                These questions appear at the end of a survey to collect qualitative feedback.
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Space>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    onClick={() => refetchQuestions()}
                                                    loading={questionsLoading}
                                                >
                                                    Refresh
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => setIsCreateModalVisible(true)}
                                                >
                                                    Add Question
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Statistics */}
                                <Row gutter={16} style={{ marginBottom: '24px' }}>
                                    <Col xs={24} sm={6}>
                                        <Card>
                                            <Statistic
                                                title="Total Questions"
                                                value={questionsTotal}
                                                loading={questionsLoading}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Card>
                                            <Statistic
                                                title="Active Questions"
                                                value={questions.filter(q => q.isActive).length}
                                                valueStyle={{ color: '#52c41a' }}
                                                loading={questionsLoading}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Card>
                                            <Statistic
                                                title="Inactive Questions"
                                                value={questions.filter(q => !q.isActive).length}
                                                valueStyle={{ color: '#ff4d4f' }}
                                                loading={questionsLoading}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Card>
                                            <Statistic
                                                title="Surveys Without Final Question"
                                                value={surveysWithoutFinalQuestion}
                                                valueStyle={{ color: surveysWithoutFinalQuestion > 0 ? '#fa8c16' : '#52c41a' }}
                                                loading={questionsLoading}
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                <Card>
                                    <Table
                                        columns={finalQuestionColumns}
                                        dataSource={questions}
                                        loading={questionsLoading}
                                        rowKey="id"
                                        pagination={{
                                            current: fqCurrentPage,
                                            pageSize: fqPageSize,
                                            total: questionsTotal,
                                            onChange: (page, size) => {
                                                setFqCurrentPage(page);
                                                setFqPageSize(size || 10);
                                            },
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} questions`,
                                        }}
                                    />
                                </Card>
                            </>
                        ),
                    },
                ]}
            />

            {/* Create Final Question Modal */}
            <Modal
                title="Create Final Question"
                open={isCreateModalVisible}
                onCancel={() => {
                    setIsCreateModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateQuestion}
                    initialValues={{ displayOrder: 0, isActive: true }}
                >
                    <Form.Item
                        label="Question Text"
                        name="text"
                        rules={[
                            { required: true, message: 'Please enter question text' },
                            { min: 10, message: 'Question must be at least 10 characters' },
                        ]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="What would you improve about this brand?"
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item
                        label="Display Order"
                        name="displayOrder"
                        tooltip="Lower numbers appear first in the selection list"
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        label="Active Status"
                        name="isActive"
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => {
                                setIsCreateModalVisible(false);
                                form.resetFields();
                            }}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={isCreating}>
                                Create
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Final Question Modal */}
            <Modal
                title="Edit Final Question"
                open={isEditModalVisible}
                onCancel={() => {
                    setIsEditModalVisible(false);
                    setSelectedQuestion(null);
                    editForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleEditQuestion}
                >
                    <Form.Item
                        label="Question Text"
                        name="text"
                        rules={[
                            { required: true, message: 'Please enter question text' },
                            { min: 10, message: 'Question must be at least 10 characters' },
                        ]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="What would you improve about this brand?"
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>

                    <Form.Item
                        label="Display Order"
                        name="displayOrder"
                        tooltip="Lower numbers appear first in the selection list"
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        label="Active Status"
                        name="isActive"
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => {
                                setIsEditModalVisible(false);
                                setSelectedQuestion(null);
                                editForm.resetFields();
                            }}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={isUpdating}>
                                Update
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Apply to All Surveys Modal */}
            <Modal
                title={
                    <span>
                        <GlobalOutlined style={{ marginRight: 8, color: '#F59E0B' }} />
                        Apply Final Question to All Surveys
                    </span>
                }
                open={isApplyToAllModalVisible}
                onCancel={() => {
                    setIsApplyToAllModalVisible(false);
                    setQuestionToApply(null);
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setIsApplyToAllModalVisible(false);
                            setQuestionToApply(null);
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="apply"
                        type="primary"
                        loading={isBulkApplying}
                        onClick={handleBulkApply}
                    >
                        Apply to All
                    </Button>,
                ]}
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <Alert
                        type="info"
                        showIcon
                        message="Bulk Apply Final Question"
                        description={
                            <div>
                                <p>This action will apply the following final question to <strong>{surveysWithoutFinalQuestion} survey(s)</strong> that currently do not have a final question configured:</p>
                                <Card size="small" style={{ marginTop: 12, marginBottom: 12, backgroundColor: '#f5f5f5' }}>
                                    <Text italic>"{questionToApply?.text}"</Text>
                                </Card>
                                <p style={{ marginBottom: 0 }}>
                                    <strong>Note:</strong> This is a migration utility. Surveys that already have a final question configured will not be affected.
                                    New responses to these surveys will include the final question.
                                </p>
                            </div>
                        }
                    />
                </div>
            </Modal>
        </div>
    );
};